import { BaseAuditLogEntry, ResourceType } from '../types/auditLogs.types';
import { ReadAuditLogsResponse } from '../types/auditLogsApi.types';
import {
  EnrichAuditLogEntry,
  EnrichmentConversation,
  EnrichmentMaps,
  EnrichmentNamedEntity,
} from '../types/enrichment.types';
import { User } from '../types/resources/common.types';

/** Builds the id-keyed `users`/`teams` lookup maps from the response enrichments. */
function buildEnrichmentMaps(enrichments: any): EnrichmentMaps {
  const users: Record<string, User> = {};
  for (const user of enrichments?.users ?? []) {
    users[user.id] = user;
  }

  const teams: Record<string, EnrichmentNamedEntity> = {};
  for (const team of enrichments?.teams ?? []) {
    teams[team.id] = team;
  }

  const conversations: Record<string, EnrichmentConversation> = {};
  for (const conversation of enrichments?.conversations ?? []) {
    conversations[conversation.id] = conversation;
  }

  return { users, teams, conversations };
}

/**
 * Flattens the response's nested `event.payload` into each top-level entry, then
 * optionally runs a resource-specific `enrichEntry` transform to resolve
 * referenced ids (e.g. agent, team) to their names using the enrichment maps.
 */
export function transformAuditLogsResponse<
  TResource extends ResourceType,
  TEntry = BaseAuditLogEntry<TResource>,
>(
  response: any,
  enrichEntry?: EnrichAuditLogEntry<TResource, TEntry>,
): ReadAuditLogsResponse<TResource, TEntry> {
  const maps = buildEnrichmentMaps(response.data.enrichments);

  return {
    ...response,
    data: {
      logs: response.data.logs.map(({ event, ...meta }): TEntry => {
        const entry = {
          ...meta,
          event_id: meta.id,
          event_timestamp: meta.timestamp,
          ...event.payload,
          activity_type: event.type,
        } as BaseAuditLogEntry<TResource>;

        return enrichEntry ? enrichEntry(entry, maps) : (entry as TEntry);
      }),
    },
  };
}
